import { Link } from "react-router-dom";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
dayjs.extend(advancedFormat);

import { FeedActionType, FeedEvent } from "../../types/GlobalInterfaces";

interface Props {
  record: FeedEvent;
  getUserName: (userID: string) => string;
  getCloudName: (cloudID: string) => string;
  onOpen: () => void;
  userLink?: string;
}

const getSecurityActionData = (feedEvent: FeedEvent) => {
  const action: FeedActionType | string =
    feedEvent.DataObject?.Action || feedEvent?.Type;

  switch (action) {
    case "user-signin":
      return {
        icon: "mdi mdi-police-badge",
        text: `signed into the ${
          feedEvent?.Subject?.split("into the")?.[1] ?? "Cloud"
        }`,
        relatedTo: null,
      };
    case "Backup Restore":
      return {
        icon: "mdi mdi-cloud-upload-outline",
        text: "performed a backup restore.",
        relatedTo: null,
      };
    default:
      return {
        icon: "mdi mdi-pencil",
        text: "Unknown Action",
        actionName: feedEvent?.RelatedTo?._RecordName,
      };
  }
};

const getRecordActionData = (feedEvent: FeedEvent) => {
  if (feedEvent?.Type !== "Module") {
    return getSecurityActionData(feedEvent);
  }

  const action = feedEvent.DataObject?.Action;

  switch (action) {
    case "record-modify":
      return {
        icon: "mdi mdi-pencil",
        text: "modified",
        relatedTo: feedEvent?.RelatedTo?._RecordName,
      };
    case "record-create":
      return {
        icon: "mdi mdi-plus",
        text: "created",
        relatedTo: feedEvent?.RelatedTo?._RecordName,
      };
    case "record-locked":
      return {
        icon: "mdi mdi-lock-outline",
        text: "locked",
        relatedTo: feedEvent?.RelatedTo?._RecordName,
      };
    case "record-unlocked":
      return {
        icon: "mdi mdi-lock-open-variant-outline",
        text: "unlocked",
        relatedTo: feedEvent?.RelatedTo?._RecordName,
      };
    case "record-delete":
      return {
        icon: "mdi mdi-delete",
        text: "deleted",
        relatedTo: feedEvent?.DataObject?.priorRecord?._RecordName,
      };

    case "record-sync":
      return {
        icon: "mdi mdi-cloud-sync-outline",
        text: "modified and synced",
        relatedTo: feedEvent?.RelatedTo?._RecordName,
      };

    case "record-shared-create":
      return {
        icon: "mdi mdi-cloud-arrow-down-outline",
        text: `was ${
          feedEvent?.DataObject?.SyncType === "Copy" ? "Copied" : "Synced"
        }`,
        relatedTo: feedEvent?.RelatedTo?._RecordName,
      };

    default:
      return {
        icon: "mdi mdi-pencil",
        text: "Unknown Action",
        actionName: feedEvent?.RelatedTo?._RecordName,
      };
  }
};

const FeedItem = ({
  record,
  getUserName,
  getCloudName,
  onOpen,
  userLink,
}: Props) => {
  const isRecordRelated = record?.Type === "Module";
  const { icon, text, relatedTo } = getRecordActionData(record);

  const userName = getUserName(record?.UserID || "");
  const cloudID = record?.DataObject?.CloudID;
  const cloudName = cloudID ? getCloudName(cloudID) : "";

  const CreateTime = (
    <div className="text-light small">
      {dayjs(record.Created).format("MMMM Do YYYY, h:mm a")}
    </div>
  );

  return (
    <div className="d-flex p-2" key={record.id}>
      <div
        className="d-flex align-items-center justify-content-center mr-2"
        style={{
          background: "var(--primary)",
          borderRadius: "50%",
          minWidth: 40,
          minHeight: 40,
          maxWidth: 40,
          maxHeight: 40,
          width: "100%",
          height: "100%",
          zIndex: 1,
        }}
      >
        <i
          className={icon}
          style={{
            fontSize: 22,
            color: "white",
          }}
        />
      </div>
      {isRecordRelated && (
        <div>
          <span style={{ fontWeight: 600, color: "var(--primary)" }}>
            {relatedTo}&nbsp;
          </span>
          {text} by&nbsp;
          {userLink ? (
            <Link to={userLink}>{userName}</Link>
          ) : (
            `${userName} ${
              record?.DataObject?.CloudID ? `from ${cloudName}` : ""
            }`
          )}
          {CreateTime}
        </div>
      )}
      {!isRecordRelated && (
        <div>
          {userLink ? <Link to={userLink}>{userName}</Link> : userName}
          &nbsp;{text}
          {CreateTime}
        </div>
      )}
      {record?.DataObject?.Action &&
        (record.DataObject.Action === "record-modify" ||
          record.DataObject.Action === "record-sync") && (
          <button
            className="ml-4 d-flex justify-content-center align-items-center"
            style={{
              border: "1px solid var(--light)",
              height: 30,
              width: 30,
              borderRadius: 5,
              background: "transparent",
            }}
            onClick={onOpen}
          >
            <i
              className="mdi mdi-chevron-right text-primary"
              style={{ fontSize: 22 }}
            />
          </button>
        )}
    </div>
  );
};

export default FeedItem;
